"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

type Lead = {
  id: number;
  name: string;
  company: string;
  phone: string;
};

const LEADS: Lead[] = [
  { id: 1, name: "Marcus Weber", company: "Acme Corp", phone: "+1 (415) 555-0183" },
  { id: 2, name: "Sarah Chen", company: "Nimbus Studio", phone: "+1 (415) 555-0219" },
  { id: 3, name: "Tom Becker", company: "Helix Labs", phone: "+1 (415) 555-0347" },
  { id: 4, name: "Lisa Roth", company: "Bluebird AG", phone: "+49 30 555 01-83" },
  { id: 5, name: "Ahmed Faris", company: "Novus Digital", phone: "+971 4 555 7291" },
  { id: 6, name: "Maria Santos", company: "Velocity", phone: "+34 91 555 4820" },
  { id: 7, name: "Raj Kapoor", company: "Orbit Analytics", phone: "+91 80 555 6174" },
  { id: 8, name: "Hannah Voss", company: "Northwind GmbH", phone: "+49 89 555 7601" },
];

const ROTATION_MS = 9000;
const DISPOSITION_AT_MS = 7500;

// ============================================================================
// Root Section
// ============================================================================

export const PowerDialerSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <LiveDialerScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("powerDialer");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <PhoneBoltIcon className="h-3.5 w-3.5" />
        Power Dialer
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Live Demo Scene
// ============================================================================

const LiveDialerScene = () => {
  const [leads, setLeads] = React.useState(LEADS);

  React.useEffect(() => {
    const id = setInterval(() => {
      setLeads((prev) => [...prev.slice(1), prev[0]]);
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  const activeLead = leads[0];

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[540px]">
        <LiveCallPane key={activeLead.id} lead={activeLead} />
        <QueuePane leads={leads} />
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Live Call pane (left)
// ----------------------------------------------------------------------------

const LiveCallPane = ({ lead }: { lead: Lead }) => {
  const t = useTranslations("powerDialer");
  const [seconds, setSeconds] = React.useState(0);
  const [phase, setPhase] = React.useState<"calling" | "disposition">("calling");

  React.useEffect(() => {
    const tick = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, []);

  React.useEffect(() => {
    const id = setTimeout(() => setPhase("disposition"), DISPOSITION_AT_MS);
    return () => clearTimeout(id);
  }, []);

  const mm = Math.floor(seconds / 60).toString().padStart(2, "0");
  const ss = (seconds % 60).toString().padStart(2, "0");

  return (
    <div className="lg:col-span-5 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          <span className="text-[10px] font-semibold tracking-wider text-emerald-400 uppercase">
            {t("live_label")}
          </span>
        </div>
        <span className="text-[10px] font-mono tabular-nums text-neutral-400">
          {mm}:{ss}
        </span>
      </div>

      {/* Active call card */}
      <div className="relative rounded-2xl border border-neutral-800 bg-neutral-900 p-5 shadow-lg overflow-hidden">
        <div className="relative">
          <div className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">
            {t("in_call")}
          </div>
          <motion.div
            key={lead.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mt-2"
          >
            <div className="text-xl font-semibold text-white">{lead.name}</div>
            <div className="text-sm text-neutral-400 mt-0.5">
              {t("active_role")}
            </div>
            <div className="text-xs text-neutral-500 mt-1 font-mono">
              {lead.phone}
            </div>
          </motion.div>

          {/* Script chip */}
          <div className="mt-4 rounded-lg border border-neutral-800 bg-neutral-950/70 p-2.5">
            <div className="flex items-center gap-1.5">
              <QuoteIcon className="h-3 w-3 text-red-400" />
              <span className="text-[9px] font-semibold uppercase tracking-wider text-neutral-400">
                {t("script_label")}
              </span>
            </div>
            <p className="mt-1 text-xs text-neutral-300 leading-relaxed">
              &ldquo;{t("script_hint")}&rdquo;
            </p>
          </div>

          {/* Buttons */}
          <div className="mt-4 flex items-center gap-1.5">
            <DialerButton label={t("btn_mute")} />
            <DialerButton label={t("btn_hangup")} danger />
            <DialerButton label={t("btn_rec")} />
          </div>
        </div>

        {/* Disposition overlay — fades in at end of cycle */}
        <AnimatePresence>
          {phase === "disposition" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 flex items-center justify-center bg-neutral-950/85 backdrop-blur-sm"
            >
              <div className="flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-200">
                <motion.span
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {t("disposition_overlay")}
                </motion.span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Local Presence badge */}
      <LocalPresenceBadge />

      <div className="flex-1" />
    </div>
  );
};

const DialerButton = ({
  label,
  danger,
}: {
  label: string;
  danger?: boolean;
}) => (
  <span
    className={cn(
      "text-[10px] font-medium px-2 py-1 rounded-md border",
      danger
        ? "text-rose-400 border-rose-900 bg-rose-950/60"
        : "text-neutral-400 border-neutral-800 bg-neutral-900"
    )}
  >
    {label}
  </span>
);

const LocalPresenceBadge = () => {
  const t = useTranslations("powerDialer");
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/70 p-3 flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        <GlobeIcon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
          {t("local_presence_label")}
        </div>
        <div className="text-sm font-mono text-neutral-200 tabular-nums">
          {t("local_presence_number")}
        </div>
      </div>
      <span className="text-[9px] uppercase tracking-wider font-semibold text-emerald-400 bg-emerald-950/60 border border-emerald-900 px-1.5 py-0.5 rounded">
        matched
      </span>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Queue pane (right)
// ----------------------------------------------------------------------------

const QueuePane = ({ leads }: { leads: Lead[] }) => {
  const t = useTranslations("powerDialer");
  const visible = leads.slice(0, 7);

  return (
    <div className="lg:col-span-7 relative overflow-hidden p-5 bg-neutral-950">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <PhoneBoltIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("queue_label")}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500">{t("queue_count")}</span>
      </div>

      {/* Lead queue */}
      <motion.ul layout className="space-y-2 min-h-[320px]">
        <AnimatePresence initial={false}>
          {visible.map((lead, i) => (
            <motion.li
              key={lead.id}
              layout
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -120 }}
              transition={{
                type: "spring",
                stiffness: 220,
                damping: 28,
                mass: 0.8,
              }}
            >
              <LeadCard
                lead={lead}
                state={i === 0 ? "active" : i === 3 ? "voicemail" : "ready"}
              />
            </motion.li>
          ))}
        </AnimatePresence>
      </motion.ul>

      {/* Stats row */}
      <div className="mt-5 pt-4 border-t border-neutral-800 grid grid-cols-4 gap-3">
        <DialsPerHour />
        <StatCell
          value={t("stat_connect_value")}
          label={t("stat_connect_label")}
        />
        <StatCell
          value={t("stat_talk_value")}
          label={t("stat_talk_label")}
        />
        <StatCell
          value={t("stat_voicemail_value")}
          label={t("stat_voicemail_label")}
        />
      </div>
    </div>
  );
};

type LeadState = "active" | "voicemail" | "ready";

const LeadCard = ({ lead, state }: { lead: Lead; state: LeadState }) => {
  const t = useTranslations("powerDialer");
  const [pulse, setPulse] = React.useState(false);

  React.useEffect(() => {
    // flash pulse when card enters "active" state
    if (state === "active") {
      setPulse(true);
      const id = setTimeout(() => setPulse(false), 600);
      return () => clearTimeout(id);
    }
  }, [state, lead.id]);

  const stateStyles: Record<LeadState, string> = {
    active:
      "border-red-500/50 bg-red-950/30 ring-1 ring-red-500/40",
    voicemail:
      "border-neutral-800 bg-neutral-900/60 opacity-70",
    ready: "border-neutral-800 bg-neutral-900/40",
  };

  const statusLabel =
    state === "active"
      ? t("status_dialing")
      : state === "voicemail"
      ? t("status_voicemail")
      : t("status_ready");

  const statusClass =
    state === "active"
      ? "text-red-300 bg-red-950/70"
      : state === "voicemail"
      ? "text-rose-400 bg-rose-950/40 line-through"
      : "text-neutral-500 bg-neutral-800/60";

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 transition-colors",
        stateStyles[state]
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          className={cn(
            "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
            state === "active"
              ? "bg-red-500/20 text-red-200 ring-1 ring-red-500/50"
              : "bg-neutral-800 text-neutral-400"
          )}
        >
          {lead.name
            .split(" ")
            .map((p) => p[0])
            .slice(0, 2)
            .join("")}
        </span>
        <div className="min-w-0">
          <div
            className={cn(
              "text-sm font-medium truncate",
              state === "active" ? "text-white" : "text-neutral-300"
            )}
          >
            {lead.name}
          </div>
          <div className="text-[11px] text-neutral-500 truncate font-mono">
            {lead.company} · {lead.phone}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {state === "active" && (
          <motion.span
            className="h-2 w-2 rounded-full bg-red-500"
            animate={{ scale: pulse ? [1, 1.8, 1] : [1, 1.4, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
          />
        )}
        <span
          className={cn(
            "text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded whitespace-nowrap",
            statusClass
          )}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  );
};

const DialsPerHour = () => {
  const t = useTranslations("powerDialer");
  const [dials, setDials] = React.useState(47);
  React.useEffect(() => {
    const id = setInterval(() => setDials((d) => d + 1), 5800);
    return () => clearInterval(id);
  }, []);
  return (
    <div>
      <motion.div
        key={dials}
        initial={{ opacity: 0.4, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="text-xl font-bold text-white tabular-nums"
      >
        {dials}
      </motion.div>
      <div className="text-[10px] text-neutral-500 mt-0.5 leading-tight">
        {t("stat_dials_label")}
      </div>
    </div>
  );
};

const StatCell = ({ value, label }: { value: string; label: string }) => (
  <div>
    <div className="text-xl font-bold text-white tabular-nums">{value}</div>
    <div className="text-[10px] text-neutral-500 mt-0.5 leading-tight">
      {label}
    </div>
  </div>
);

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconAutoDial /> },
  { key: "t2", icon: <IconVoicemail /> },
  { key: "t3", icon: <IconGlobe /> },
  { key: "t4", icon: <IconIdCard /> },
  { key: "t5", icon: <IconScript /> },
  { key: "t6", icon: <IconCheckCircle /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("powerDialer");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function PhoneBoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M6.6 10.8c1.5 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C9.8 21 3 14.2 3 5.9c0-.6.4-1 1-1h3.4c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.4 0 .7-.2 1l-2.2 1.4z" />
      <path d="M17 2l-4 6h4l-3 6 8-8h-5l4-4z" opacity="0.5" />
    </svg>
  );
}

function QuoteIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M7 7h3v3c0 2-1 3-3 3v2c3 0 5-2 5-5V7H7v0zm8 0h3v3c0 2-1 3-3 3v2c3 0 5-2 5-5V7h-5v0z" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a13 13 0 0 1 0 18M12 3a13 13 0 0 0 0 18" />
    </svg>
  );
}

function IconAutoDial() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}

function IconVoicemail() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="6" cy="13" r="4" />
      <circle cx="18" cy="13" r="4" />
      <path d="M6 17h12" />
      <path d="M16 5l4 4M20 5l-4 4" />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="12" cy="10" r="7" />
      <path d="M12 17v4M8 21h8" />
      <path d="M5 10h14M12 3a10 10 0 0 1 0 14M12 3a10 10 0 0 0 0 14" />
    </svg>
  );
}

function IconIdCard() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="12" r="2.5" />
      <path d="M14 10h5M14 13h4M14 16h5M5 18c.6-1.6 2-2.6 4-2.6s3.4 1 4 2.6" />
    </svg>
  );
}

function IconScript() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M6 3h11a2 2 0 0 1 2 2v13a3 3 0 0 0 3 3H8a3 3 0 0 1-3-3V5a2 2 0 0 1 1-2z" />
      <path d="M9 8h7M9 12h7M9 16h4" />
    </svg>
  );
}

function IconCheckCircle() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
}
